import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BookOpen, Wallet, MessageSquare, AlertCircle, Sparkles, GraduationCap, ChevronRight, FileText, Award, QrCode } from "lucide-react";
import { api, fmtGNF } from "@/lib/api";
import AIInsightsCard from "@/components/AIInsightsCard";

const downloadPdf = (url, filename) => {
  const token = localStorage.getItem("mazid_token");
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
  fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    });
};

export default function ParentDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/parent").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      <div>
        <div className="chip mb-2"><Users className="h-3 w-3" /><span>Espace parent</span></div>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] tracking-tight">Bonjour, voici la situation de vos enfants</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-children">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Mes enfants</div>
          <div className="font-heading font-extrabold text-3xl text-[#1E3A8A]">{data.children_count}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-avg">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Moyenne</div>
          <div className="font-heading font-extrabold text-3xl text-[#166534]">{data.average_grade}/20</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-paid">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Payé cette année</div>
          <div className="font-heading font-extrabold text-xl text-[#166534]">{fmtGNF(data.paid_total)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="kpi-pending-fees">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">À régler</div>
          <div className="font-heading font-extrabold text-xl text-[#EA580C]">{fmtGNF(data.pending_total)}</div>
        </div>
      </div>

      {/* AI Insights pour parent */}
      <AIInsightsCard accentColor="#1E3A8A" />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-lg text-[#1E3A8A]">Mes enfants</h3>
            <Link to="/dashboard/enfants" className="text-sm text-[#166534] font-semibold flex items-center gap-1 hover:gap-2 transition-all">Tout voir <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="space-y-3">
            {data.children.map((c) => (
              <div key={c.id} className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`child-card-${c.id}`}>
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${c.gender === "M" ? "bg-[#1E3A8A]" : "bg-[#EA580C]"}`}>
                    {c.first_name[0]}{c.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0F172A]">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-slate-500">{c.class_name} · {c.birth_date}</div>
                  </div>
                  <Link to={`/dashboard/notes?student=${c.id}`} className="text-xs px-3 py-1.5 rounded-md bg-[#166534] text-white font-semibold hover:bg-[#14532d]">Voir les notes</Link>
                </div>
                {/* Boutons PDF officiels */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                  <button
                    onClick={() => downloadPdf(`/api/students/${c.id}/bulletin?period=Trimestre%201`, `bulletin-${c.last_name}-T1.pdf`)}
                    className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold hover:bg-[#1E3A8A] hover:text-white transition flex items-center gap-1"
                    data-testid={`bulletin-t1-${c.id}`}
                  >
                    <FileText className="h-3 w-3" /> Bulletin T1
                  </button>
                  <button
                    onClick={() => downloadPdf(`/api/students/${c.id}/bulletin?period=Trimestre%202`, `bulletin-${c.last_name}-T2.pdf`)}
                    className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#1E3A8A] text-[#1E3A8A] font-semibold hover:bg-[#1E3A8A] hover:text-white transition flex items-center gap-1"
                    data-testid={`bulletin-t2-${c.id}`}
                  >
                    <FileText className="h-3 w-3" /> Bulletin T2
                  </button>
                  <button
                    onClick={() => downloadPdf(`/api/students/${c.id}/certificate`, `certificat-${c.last_name}.pdf`)}
                    className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#EA580C] text-[#EA580C] font-semibold hover:bg-[#EA580C] hover:text-white transition flex items-center gap-1"
                    data-testid={`certificate-${c.id}`}
                  >
                    <Award className="h-3 w-3" /> Certificat de scolarité
                  </button>
                  <button
                    onClick={() => downloadPdf(`/api/students/${c.id}/student-card`, `carte-etudiant-${c.last_name}.pdf`)}
                    className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#166534] text-[#166534] font-semibold hover:bg-[#166534] hover:text-white transition flex items-center gap-1"
                    data-testid={`student-card-${c.id}`}
                  >
                    <QrCode className="h-3 w-3" /> Carte étudiant (QR)
                  </button>
                </div>
              </div>
            ))}
            {data.children.length === 0 && (
              <div className="text-center text-slate-500 py-8">
                Aucun enfant inscrit pour l&apos;instant.
                <Link to="/inscription" className="block mt-3 text-[#EA580C] font-semibold">Inscrire un enfant →</Link>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <Link to="/dashboard/paiements" className="block bg-gradient-to-br from-[#166534] to-[#14532d] text-white rounded-xl p-6 hover:from-[#14532d] hover:to-[#0f3a22] transition-all" data-testid="cta-pay">
            <Wallet className="h-8 w-8 mb-4 text-[#FBBF24]" />
            <div className="font-heading font-bold text-lg mb-1">Payer les frais</div>
            <div className="text-sm opacity-80">Scolarité, inscription, manuels…</div>
          </Link>
          <Link to="/dashboard/ai" className="block bg-gradient-to-br from-[#EA580C] to-orange-700 text-white rounded-xl p-6 hover:opacity-95 transition" data-testid="cta-ai">
            <Sparkles className="h-8 w-8 mb-4 text-white" />
            <div className="font-heading font-bold text-lg mb-1">MAZID AI Assistant</div>
            <div className="text-sm opacity-90">Posez vos questions, recevez des conseils.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
